/**
 * In-browser PiperTTS voices (https://github.com/rhasspy/piper).
 *
 * Piper is a fast, local neural TTS engine. Its voice models are plain ONNX
 * files, and `@mintplex-labs/piper-tts-web` runs them entirely on-device via
 * ONNX Runtime WebAssembly — no audio or text leaves the browser, which keeps
 * the app's "audio is never stored, transcription costs nothing extra" story
 * intact. Models download once from Hugging Face (`rhasspy/piper-voices`,
 * ~60 MB per voice) and are cached in Origin Private File System, so repeat
 * visits work offline.
 *
 * Two honest limitations are surfaced, not hidden:
 *
 *  - Upstream Piper has **no female Portuguese voice** (open issue
 *    rhasspy/piper#768) and no female Spanish voice either — every `pt_BR`
 *    and `es_*` voice is male. When the preferred gender is unavailable the
 *    resolver falls back across genders and says so.
 *  - The WASM runtime plus the first model download needs a desktop-class
 *    browser. When anything in that chain is missing, `isPiperSupported()`
 *    is false and the normal chain (browser `speechSynthesis`, then server
 *    TTS) takes over.
 */

import type { LanguageCode } from "@shared/types/language";

export type PiperGender = "female" | "male";

export interface PiperVoiceOption {
  /** Exact `voiceId` accepted by `piper-tts-web`, e.g. `pt_BR-faber-medium`. */
  readonly id: string;
  /** Short human label, e.g. "Faber". */
  readonly label: string;
  readonly gender: PiperGender;
  /** Model quality tier, e.g. "medium". Higher usually sounds better. */
  readonly quality: string;
}

export interface PiperCatalogEntry {
  readonly voices: readonly PiperVoiceOption[];
  /** Shown under the picker when the catalog cannot satisfy a preference. */
  readonly note?: string;
}

/**
 * Curated defaults per registry language code (full code first, base code as
 * fallback). Every id below exists in the `piper-tts-web` voice list, so a
 * catalog hit never 404s — unverified ids would fail only at download time.
 */
export const PIPER_CATALOG: Readonly<Record<string, PiperCatalogEntry>> = {
  en: {
    voices: [
      { id: "en_US-hfc_female-medium", label: "Female (US)", gender: "female", quality: "medium" },
      { id: "en_US-hfc_male-medium", label: "Male (US)", gender: "male", quality: "medium" },
      { id: "en_US-amy-medium", label: "Amy (US)", gender: "female", quality: "medium" },
      { id: "en_US-ryan-medium", label: "Ryan (US)", gender: "male", quality: "medium" },
      { id: "en_GB-alba-medium", label: "Alba (GB)", gender: "female", quality: "medium" },
      { id: "en_GB-alan-medium", label: "Alan (GB)", gender: "male", quality: "medium" },
    ],
  },
  "pt-BR": {
    // NOTE: only voices present in the engine's downloadable map count.
    // `pt_BR-cadu-medium` exists upstream but is absent from this
    // piper-tts-web release's map, so requesting it 404s — it is deliberately
    // not listed (a custom id override still lets a future release try it).
    voices: [
      { id: "pt_BR-faber-medium", label: "Faber", gender: "male", quality: "medium" },
      { id: "pt_BR-edresson-low", label: "Edresson", gender: "male", quality: "low" },
    ],
    note: "Upstream Piper has no female Brazilian-Portuguese voice yet, so every option here is male.",
  },
  pt: {
    voices: [
      { id: "pt_BR-faber-medium", label: "Faber (BR)", gender: "male", quality: "medium" },
      { id: "pt_BR-edresson-low", label: "Edresson (BR)", gender: "male", quality: "low" },
      { id: "pt_PT-tugao-medium", label: "Tugão (PT)", gender: "male", quality: "medium" },
    ],
    note: "Upstream Piper has no female Portuguese voice yet, so every option here is male.",
  },
  es: {
    voices: [
      { id: "es_MX-claude-high", label: "Claude (MX)", gender: "male", quality: "high" },
      { id: "es_ES-sharvard-medium", label: "Sharvard (ES)", gender: "male", quality: "medium" },
      { id: "es_ES-davefx-medium", label: "Dave (ES)", gender: "male", quality: "medium" },
      { id: "es_MX-ald-medium", label: "Ald (MX)", gender: "male", quality: "medium" },
    ],
    note: "Upstream Piper has no female Spanish voice yet, so every option here is male.",
  },
  ar: {
    voices: [{ id: "ar_JO-kareem-medium", label: "Kareem", gender: "male", quality: "medium" }],
    note: "Single curated Arabic voice for this app.",
  },
  zh: {
    voices: [{ id: "zh_CN-huayan-medium", label: "Huayan", gender: "female", quality: "medium" }],
    note: "Single curated Mandarin voice for this app.",
  },
  ru: {
    voices: [{ id: "ru_RU-ruslan-medium", label: "Ruslan", gender: "male", quality: "medium" }],
    note: "Single curated Russian voice for this app.",
  },
  "pt-PT": {
    voices: [{ id: "pt_PT-tugao-medium", label: "Tugão", gender: "male", quality: "medium" }],
    note: "Single curated European-Portuguese voice for this app.",
  },
  fr: {
    voices: [
      { id: "fr_FR-siwis-medium", label: "Siwis", gender: "female", quality: "medium" },
      { id: "fr_FR-tom-medium", label: "Tom", gender: "male", quality: "medium" },
      { id: "fr_FR-gilles-low", label: "Gilles", gender: "male", quality: "low" },
    ],
  },
  de: {
    voices: [
      { id: "de_DE-kerstin-low", label: "Kerstin", gender: "female", quality: "low" },
      { id: "de_DE-eva_k-x_low", label: "Eva", gender: "female", quality: "x_low" },
      { id: "de_DE-thorsten-medium", label: "Thorsten", gender: "male", quality: "medium" },
    ],
  },
  it: {
    voices: [
      { id: "it_IT-paola-medium", label: "Paola", gender: "female", quality: "medium" },
      { id: "it_IT-riccardo-x_low", label: "Riccardo", gender: "male", quality: "x_low" },
    ],
  },
  nl: {
    voices: [
      { id: "nl_BE-nathalie-medium", label: "Nathalie (BE)", gender: "female", quality: "medium" },
      { id: "nl_BE-rdh-medium", label: "RDH (BE)", gender: "male", quality: "medium" },
    ],
  },
};

/**
 * The Portuguese voice hosted on the deployment itself (`/api/voices`,
 * R2-backed) and seeded into the on-device cache on first run — the one
 * voice this app guarantees is ready without ever touching a third party.
 */
export const BUNDLED_PT_VOICE = "pt_BR-faber-medium";

/**
 * Voice ids hosted on the deployment itself (`/api/voices`, R2-backed) —
 * mirrors the bucket contents. Seeding prefers these over the public mirror:
 * same edge network as the API, immutable cache, no third-party dependency.
 */
export const R2_HOSTED_VOICES: ReadonlySet<string> = new Set([
  "pt_BR-faber-medium",
  "es_MX-claude-high",
  "ar_JO-kareem-medium",
  "zh_CN-huayan-medium",
  "ru_RU-ruslan-medium",
]);

/**
 * The default voice lineup, in display order: one best voice per language.
 * Thai, Hindi, Japanese and Korean have no model in this engine release, so
 * they resolve to no Piper voice and use the built-in voices instead.
 */
export const DEFAULT_VOICE_LANGUAGES: readonly LanguageCode[] = ["es", "pt-BR", "ar", "th", "hi", "ja", "ko", "zh-Hans", "ru"];

export interface ResolvedPiperVoice {
  readonly voiceId: string | null;
  /** True when the pick does not match the requested gender (or catalog). */
  readonly fellBack: boolean;
  /** Human-readable explanation of the pick, or null when it is exact. */
  readonly note: string | null;
  /** The catalog option behind the pick, when there is one. */
  readonly option: PiperVoiceOption | null;
}

/**
 * Pick the Piper voice for a target language and preferred gender.
 *
 * Pure and synchronous so it can be unit-tested: `available` is the list of
 * voice ids the runtime reports (null when never fetched — the catalog is
 * trusted, since every catalog id is verified against the bundled list).
 * A `customVoiceId` override always wins; it is the escape hatch for voices
 * added upstream later (e.g. a future female `pt_BR` voice).
 */
export function resolvePiperVoiceId(options: {
  language: string;
  gender: PiperGender;
  customVoiceId?: string | null;
  available?: readonly string[] | null;
}): ResolvedPiperVoice {
  const custom = (options.customVoiceId ?? "").trim();
  if (custom !== "") {
    return { voiceId: custom, fellBack: false, note: null, option: findCatalogOption(custom) };
  }

  const language = options.language.trim();
  const base = language.split(/[-_]/)[0]?.toLowerCase() ?? "";
  const entry = PIPER_CATALOG[language] ?? PIPER_CATALOG[base] ?? null;
  const available = options.available ?? null;
  const known = (id: string): boolean => available === null || available.includes(id);

  if (entry) {
    const ordered = [...entry.voices].sort((a, b) => Number(b.gender === options.gender) - Number(a.gender === options.gender));
    const pick = ordered.find((voice) => known(voice.id)) ?? null;
    if (pick) {
      const fellBack = pick.gender !== options.gender;
      return {
        voiceId: pick.id,
        fellBack,
        note: fellBack ? (entry.note ?? `No ${options.gender} voice for this language — using ${pick.label}.`) : null,
        option: pick,
      };
    }
  }

  // No curated entry (or none downloadable): prefix-match whatever the
  // runtime knows, most-specific prefix first, deterministic order.
  if (available) {
    const prefixes: string[] = [];
    const underscored = language.replace(/-/g, "_");
    if (underscored.includes("_")) prefixes.push(underscored);
    if (base !== "") prefixes.push(base);
    const sorted = [...available].sort();
    for (const prefix of prefixes) {
      const hit = sorted.find((id) => id.toLowerCase().startsWith(prefix.toLowerCase()));
      if (hit) {
        return {
          voiceId: hit,
          fellBack: true,
          note: "No curated voice for this language — using the closest available Piper voice.",
          option: findCatalogOption(hit),
        };
      }
    }
  }

  return { voiceId: null, fellBack: true, note: "Piper has no voice for this language on this device.", option: null };
}

function findCatalogOption(voiceId: string): PiperVoiceOption | null {
  for (const entry of Object.values(PIPER_CATALOG)) {
    const found = entry.voices.find((voice) => voice.id === voiceId);
    if (found) return found;
  }
  return null;
}

/**
 * The OPFS file names the engine reads for a voice — the same names its own
 * downloader writes, so a file seeded here is indistinguishable from one the
 * engine fetched itself.
 */
export function piperModelFiles(voiceId: string): readonly [string, string] {
  return [`${voiceId}.onnx`, `${voiceId}.onnx.json`];
}

/** Minimum plausible bytes: anything smaller is a failed download, not a model. */
const MIN_MODEL_BYTES: Readonly<Record<string, number>> = { ".onnx": 1_000_000, ".onnx.json": 100 };

async function opfsPiperDir(create: boolean): Promise<FileSystemDirectoryHandle | null> {
  try {
    const root = await navigator.storage.getDirectory();
    return await root.getDirectoryHandle("piper", { create });
  } catch {
    return null;
  }
}

/**
 * Seed a voice into the engine's OPFS cache from a self-hosted base URL
 * (the deployment's `/api/voices`, backed by R2). Files already on disk are
 * skipped; corrupt stubs are re-fetched. Resolves `"cached"` when nothing
 * was downloaded.
 */
export async function seedVoiceFiles(
  voiceId: string,
  baseUrl: string,
  onProgress?: (fraction: number, file: string) => void,
): Promise<"seeded" | "cached"> {
  const dir = await opfsPiperDir(true);
  if (!dir) throw new Error("This browser blocked local file storage, so voices cannot be cached.");
  let fetched = false;
  for (const name of piperModelFiles(voiceId)) {
    const ext = name.endsWith(".onnx.json") ? ".onnx.json" : ".onnx";
    const minBytes = MIN_MODEL_BYTES[ext] ?? 1;
    try {
      const existing = await (await dir.getFileHandle(name)).getFile();
      if (existing.size >= minBytes) {
        onProgress?.(1, name);
        continue;
      }
    } catch {
      // Missing — download below.
    }
    const response = await fetch(`${baseUrl}/${name}`);
    if (!response.ok || !response.body) {
      throw new Error(`Voice server returned ${response.status} for ${name}.`);
    }
    const total = Number(response.headers.get("content-length") ?? 0);
    const reader = response.body.getReader();
    const chunks: Uint8Array[] = [];
    let loaded = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) {
        chunks.push(value);
        loaded += value.length;
        if (total > 0) onProgress?.(Math.min(1, loaded / total), name);
      }
    }
    const handle = await dir.getFileHandle(name, { create: true });
    const writable = await handle.createWritable();
    await writable.write(new Blob(chunks.map((chunk) => chunk.buffer.slice(chunk.byteOffset, chunk.byteOffset + chunk.byteLength) as ArrayBuffer)));
    await writable.close();
    fetched = true;
    onProgress?.(1, name);
  }
  return fetched ? "seeded" : "cached";
}

/** "pt_BR-faber-medium" → "Faber · male · medium". Falls back to the raw id. */
export function describePiperVoice(voiceId: string): string {
  const option = findCatalogOption(voiceId);
  if (option) return `${option.label} · ${option.gender} · ${option.quality}`;
  const parts = voiceId.split("-");
  const name = parts[1] ?? voiceId;
  const quality = parts[2] ?? "";
  return quality !== "" ? `${name} · ${quality}` : name;
}

/** True when this browser can plausibly run the Piper WASM runtime. */
export function isPiperSupported(): boolean {
  if (typeof window === "undefined") return false;
  const scope = window as unknown as Record<string, unknown>;
  return (
    typeof scope["Worker"] !== "undefined" &&
    typeof WebAssembly !== "undefined" &&
    typeof fetch !== "undefined" &&
    typeof Audio !== "undefined"
  );
}

// ---------------------------------------------------------------------------
//  Speaker: synthesis + serial playback
// ---------------------------------------------------------------------------

export type PiperStatus = "idle" | "downloading" | "ready" | "speaking" | "error" | "blocked" | "unsupported";

export interface PiperState {
  readonly status: PiperStatus;
  /** 0..1 while downloading a model. */
  readonly progress: number;
  readonly voiceId: string | null;
  readonly detail: string | null;
}

export const IDLE_PIPER_STATE: PiperState = { status: "idle", progress: 0, voiceId: null, detail: null };

interface PiperProgress {
  readonly url: string;
  readonly total: number;
  readonly loaded: number;
}

interface PiperSession {
  predict(text: string): Promise<Blob>;
}

interface PiperSessionCtor {
  create(options: {
    voiceId: string;
    progress?: (progress: PiperProgress) => void;
    logger?: (text: string) => void;
    wasmPaths?: { onnxWasm: string; piperData: string; piperWasm: string };
  }): Promise<PiperSession>;
  _instance: unknown;
}

interface PiperLib {
  TtsSession: PiperSessionCtor;
  PATH_MAP: Readonly<Record<string, string>>;
  WASM_BASE: string;
  download(voiceId: string, onProgress?: (progress: PiperProgress) => void): Promise<void>;
  voices(): Promise<Array<{ key: string }>>;
  stored(): Promise<string[]>;
}

/**
 * ONNX Runtime WASM must match the bundled `onnxruntime-web` major version:
 * the engine pins its runtime files to cdnjs 1.18 while npm ships 1.30, and
 * that skew fails session creation. Bump this alongside the dependency.
 */
const ORT_WASM_BASE = "https://cdn.jsdelivr.net/npm/onnxruntime-web@1.30.0/dist/";

/**
 * Serial Piper playback queue.
 *
 * The heavy modules (`piper-tts-web`, ONNX WASM, the voice model) load lazily
 * on first `speak()`, so the app bundle stays small and a user who never
 * enables voice output pays nothing. Sessions are managed per voice because
 * the engine reuses a process-wide singleton: asking it for a second voice
 * without resetting would keep synthesizing with the first voice's model.
 */
export class PiperSpeaker {
  #lib: PiperLib | null = null;
  #loading: Promise<PiperLib> | null = null;
  #available: string[] | null = null;
  #sessionVoiceId: string | null = null;
  #sessionReady: Promise<PiperSession> | null = null;
  #queue: string[] = [];
  #current: HTMLAudioElement | null = null;
  #speaking = false;
  #muted = false;
  #state: PiperState = IDLE_PIPER_STATE;
  #onStateChange: ((state: PiperState) => void) | null = null;

  set onStateChange(handler: ((state: PiperState) => void) | null) {
    this.#onStateChange = handler;
  }

  get state(): PiperState {
    return this.#state;
  }

  get availableVoices(): readonly string[] | null {
    return this.#available;
  }

  setMuted(muted: boolean): void {
    this.#muted = muted;
    if (muted) this.cancel();
  }

  /** The voice ids the runtime knows about (network on first call). */
  async listVoices(): Promise<string[]> {
    if (this.#available) return this.#available;
    const lib = await this.#load();
    try {
      const voices = await lib.voices();
      this.#available = voices.map((voice) => voice.key).filter((key) => typeof key === "string");
    } catch {
      // Offline with nothing cached: `voices()` falls back to its bundled
      // list in most builds, but never assume — an empty list just means the
      // catalog is authoritative until the network returns.
      this.#available ??= [];
    }
    return this.#available;
  }

  /** Voice ids already cached on this device (instant, offline). */
  async cachedVoices(): Promise<string[]> {
    try {
      return await (await this.#load()).stored();
    } catch {
      return [];
    }
  }

  /**
   * Pre-download a voice (with progress) and warm its inference session, so
   * the first utterance synthesizes immediately instead of after a ~60 MB
   * download plus WASM startup. Prefers the deployment's own voice hosting
   * (`baseUrl`, R2-backed and edge-cached) and falls back to the public
   * mirror only when self-hosting is unreachable — a dev Worker with an
   * empty local bucket, for example.
   */
  async ensureVoice(voiceId: string, opts: { baseUrl?: string; onProgress?: (fraction: number) => void } = {}): Promise<void> {
    const lib = await this.#load();
    assertDownloadable(lib, voiceId);
    this.#setState({ status: "downloading", progress: 0, voiceId, detail: "Downloading voice model…" });
    try {
      if (opts.baseUrl) {
        try {
          await this.seedVoice(voiceId, opts.baseUrl, opts.onProgress);
        } catch {
          await this.#mirrorDownload(lib, voiceId, opts.onProgress);
        }
      } else {
        await this.#mirrorDownload(lib, voiceId, opts.onProgress);
      }
      await this.#ensureSession(lib, voiceId, undefined);
      this.#setState({ status: "ready", progress: 1, voiceId, detail: null });
    } catch (error) {
      const detail = error instanceof Error ? error.message : "Voice download failed.";
      this.#setState({ status: "error", progress: 0, voiceId, detail });
      throw error;
    }
  }

  /**
   * Seed a voice from self-hosted files into the engine cache, then warm
   * its session. After this returns, speaking needs no network at all.
   */
  async seedVoice(voiceId: string, baseUrl: string, onProgress?: (fraction: number) => void): Promise<void> {
    const lib = await this.#load();
    assertDownloadable(lib, voiceId);
    this.#setState({ status: "downloading", progress: 0, voiceId, detail: "Downloading voice model…" });
    try {
      await seedVoiceFiles(voiceId, baseUrl, (fraction, file) => {
        this.#setState({
          status: "downloading",
          progress: fraction,
          voiceId,
          detail: file.endsWith(".json") ? "Preparing voice…" : `Downloading voice ${Math.round(fraction * 100)}%`,
        });
        onProgress?.(fraction);
      });
      await this.#ensureSession(lib, voiceId, undefined);
      this.#setState({ status: "ready", progress: 1, voiceId, detail: null });
    } catch (error) {
      const detail = error instanceof Error ? error.message : "Voice download failed.";
      console.error(`[dream-tongue] Seeding Piper voice "${voiceId}" failed:`, detail);
      this.#setState({ status: "error", progress: 0, voiceId, detail });
      throw error;
    }
  }

  async #mirrorDownload(lib: PiperLib, voiceId: string, onProgress?: (fraction: number) => void): Promise<void> {
    await lib.download(voiceId, (progress) => {
      const fraction = progress.total > 0 ? Math.min(1, progress.loaded / progress.total) : 0;
      this.#setState({ status: "downloading", progress: fraction, voiceId, detail: "Downloading voice model…" });
      onProgress?.(fraction);
    });
  }

  /**
   * Warm an already-cached voice without spending mobile data: if the model
   * is on disk, initialize WASM + the session now so the first line is fast.
   * Resolves `"ready"` when warmed, `"not-cached"` when there is nothing to
   * warm (the UI then nudges toward an explicit download).
   */
  async prewarm(voiceId: string): Promise<"ready" | "not-cached"> {
    const lib = await this.#load();
    if (!Object.hasOwn(lib.PATH_MAP, voiceId)) return "not-cached";
    if (!(await this.cachedVoices()).includes(voiceId)) return "not-cached";
    await this.#ensureSession(lib, voiceId, undefined);
    if (this.#state.status !== "speaking") {
      this.#setState({ status: "ready", progress: 1, voiceId, detail: null });
    }
    return "ready";
  }

  /**
   * Synthesize and enqueue playback. Resolves false when muted, empty, or
   * unsupported; throws when synthesis itself fails so callers can fall back
   * to the next TTS path.
   */
  async speak(text: string, voiceId: string): Promise<boolean> {
    if (this.#muted || text.trim() === "" || !isPiperSupported()) return false;
    const lib = await this.#load();
    assertDownloadable(lib, voiceId);
    this.#setState({ status: "downloading", progress: 0, voiceId, detail: "Preparing voice…" });
    try {
      // The session chunks long text itself and returns one merged clip.
      const session = await this.#ensureSession(lib, voiceId, (progress) => {
        const fraction = progress.total > 0 ? Math.min(1, progress.loaded / progress.total) : 0;
        this.#setState({ status: "downloading", progress: fraction, voiceId, detail: "Synthesizing…" });
      });
      this.#enqueue(await session.predict(text));
      this.#setState({ status: this.#speaking ? "speaking" : "ready", progress: 1, voiceId, detail: null });
      return true;
    } catch (error) {
      const detail = error instanceof Error ? error.message : "Piper synthesis failed.";
      console.error(`[dream-tongue] Piper voice "${voiceId}" failed:`, detail);
      this.#setState({ status: "error", progress: 0, voiceId, detail });
      throw error;
    }
  }

  cancel(): void {
    this.#queue.forEach((url) => URL.revokeObjectURL(url));
    this.#queue = [];
    if (this.#current) {
      this.#current.pause();
      const src = this.#current.src;
      this.#current.removeAttribute("src");
      if (src.startsWith("blob:")) URL.revokeObjectURL(src);
      this.#current = null;
    }
    if (this.#speaking) {
      this.#speaking = false;
      this.#emit({ status: "ready", progress: 1, voiceId: this.#state.voiceId, detail: null });
    }
  }

  /**
   * The engine holds one process-wide session bound to whichever voice was
   * initialized first — switching voices without resetting it keeps
   * synthesizing with the stale model (wrong voice, wrong phonemes).
   */
  async #ensureSession(lib: PiperLib, voiceId: string, onProgress: ((progress: PiperProgress) => void) | undefined): Promise<PiperSession> {
    if (this.#sessionReady && this.#sessionVoiceId === voiceId) return this.#sessionReady;
    // Drop the singleton so the new voice gets a fresh model + session.
    lib.TtsSession._instance = null;
    this.#sessionVoiceId = voiceId;
    this.#sessionReady = lib.TtsSession.create({
      voiceId,
      ...(onProgress ? { progress: onProgress } : {}),
      wasmPaths: {
        onnxWasm: ORT_WASM_BASE,
        piperData: `${lib.WASM_BASE}.data`,
        piperWasm: `${lib.WASM_BASE}.wasm`,
      },
    }).catch((error: unknown) => {
      this.#sessionReady = null;
      this.#sessionVoiceId = null;
      throw error;
    });
    return this.#sessionReady;
  }

  async #load(): Promise<PiperLib> {
    if (this.#lib) return this.#lib;
    if (!this.#loading) {
      this.#loading = import("@mintplex-labs/piper-tts-web").then(
        (module) => module as unknown as PiperLib,
      );
    }
    this.#lib = await this.#loading;
    return this.#lib;
  }

  #enqueue(blob: Blob): void {
    const url = URL.createObjectURL(blob);
    this.#queue.push(url);
    if (!this.#speaking) void this.#pump();
  }

  async #pump(): Promise<void> {
    const next = this.#queue.shift();
    if (!next) {
      this.#speaking = false;
      this.#emit({ status: "ready", progress: 1, voiceId: this.#state.voiceId, detail: null });
      return;
    }
    this.#speaking = true;
    this.#emit({ status: "speaking", progress: 1, voiceId: this.#state.voiceId, detail: null });
    const element = new Audio(next);
    this.#current = element;
    const done = (revoke: boolean): void => {
      if (this.#current === element) this.#current = null;
      if (revoke) URL.revokeObjectURL(next);
      void this.#pump();
    };
    element.onended = () => done(true);
    element.onerror = () => done(true);
    try {
      await element.play();
    } catch {
      // Autoplay blocked (utterance finished long after the tap gesture).
      // Keep the queue — the user can replay from the transcript — but say so.
      this.#queue.unshift(next);
      this.#current = null;
      this.#speaking = false;
      this.#emit({ status: "blocked", progress: 1, voiceId: this.#state.voiceId, detail: "Playback was blocked. Tap the speaker icon to hear it." });
    }
  }

  #setState(patch: PiperState): void {
    this.#state = patch;
    this.#onStateChange?.(patch);
  }

  #emit(patch: PiperState): void {
    this.#setState(patch);
  }
}

/**
 * Fail fast with a usable message when a voice id is not downloadable in
 * this engine release. Without this, the engine fetches a 404 error page,
 * caches it as the model, and dies later in JSON parsing with gibberish.
 */
function assertDownloadable(lib: PiperLib, voiceId: string): void {
  if (!Object.hasOwn(lib.PATH_MAP, voiceId)) {
    throw new Error(`Piper has no downloadable voice "${voiceId}" in this release. Pick one from the voice list or clear the custom override.`);
  }
}

/** Split long text at sentence boundaries so no single inference stalls. */
export function chunkText(text: string, maxChars: number): string[] {
  const clean = text.trim().replace(/\s+/g, " ");
  if (clean.length <= maxChars) return clean === "" ? [] : [clean];
  const sentences = clean.match(/[^.!?…]+[.!?…]+["”)]?|\S[^.!?…]*$/g) ?? [clean];
  const chunks: string[] = [];
  let current = "";
  for (const sentence of sentences) {
    const piece = sentence.trim();
    if (piece === "") continue;
    if ((current + " " + piece).trim().length > maxChars && current !== "") {
      chunks.push(current.trim());
      current = piece;
    } else {
      current = `${current} ${piece}`;
    }
  }
  if (current.trim() !== "") chunks.push(current.trim());
  return chunks.length > 0 ? chunks : [clean.slice(0, maxChars)];
}
